import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr, API, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Download, Loader2, X, Pencil, Image as ImageIcon, FileText, Receipt, FileSignature, ChevronLeft, ChevronRight, Calculator, Users2, Building2, Database, FileSpreadsheet, MessageSquare, Palette, MapPin, Landmark, Clock, TimerReset, HardDriveDownload } from "lucide-react";
import KalkulatorMasterData from "@/components/KalkulatorMasterData";
import BackupPanel from "@/components/BackupPanel";
import PipelineAutomationSettings from "@/components/PipelineAutomationSettings";

export default function Admin() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const isOwnerOrChief = user?.role === "owner" || user?.role === "chief";
  const isManager = user?.role === "manager";
  const isAdminOrOwner = isAdmin || isOwnerOrChief;
  const canManageUsers = isAdmin || isOwnerOrChief;

  // Navigasi Pengaturan: dikelompokkan per kategori (sidebar di desktop, list
  // bertingkat ala iOS Settings di mobile). Tiap item dicek permission-nya sendiri
  // supaya grup otomatis kosong/hilang kalau tidak ada satupun item yang boleh diakses.
  const SETTINGS_GROUPS = [
    {
      title: "Tim & Cabang",
      items: [
        canManageUsers && { value: "users", label: "Pengguna", icon: Users2 },
        !isManager && { value: "branches", label: "Cabang", icon: Building2 },
      ].filter(Boolean),
    },
    {
      title: "Data Master",
      items: [
        !isManager && { value: "master", label: "Master Data", icon: Database },
        !isManager && { value: "excel", label: "Import Excel", icon: FileSpreadsheet },
        !isManager && { value: "wa", label: "Template WA", icon: MessageSquare },
      ].filter(Boolean),
    },
    {
      title: "Branding & Dokumen",
      items: [
        isAdminOrOwner && { value: "branding", label: "Branding", icon: Palette },
        isAdminOrOwner && { value: "doc-logo", label: "Logo Dokumen", icon: ImageIcon },
        isAdminOrOwner && { value: "doc-address", label: "Alamat", icon: MapPin },
        isAdminOrOwner && { value: "doc-sign", label: "TTD & Cap", icon: FileSignature },
        isAdminOrOwner && { value: "doc-bank", label: "Rekening", icon: Landmark },
        isAdminOrOwner && { value: "doc-sph", label: "Template Dokumen", icon: FileText },
      ].filter(Boolean),
    },
    {
      title: "Otomasi & Laporan",
      items: [
        isAdminOrOwner && { value: "laporan", label: "Laporan Terjadwal", icon: Clock },
        isAdminOrOwner && { value: "otomasi-pipeline", label: "Otomasi Pipeline", icon: TimerReset },
      ].filter(Boolean),
    },
    {
      title: "Lanjutan",
      items: [
        isOwnerOrChief && { value: "rab-kalkulator", label: "RAB Kalkulator", icon: Calculator },
        isOwnerOrChief && { value: "backup", label: "Backup", icon: HardDriveDownload },
      ].filter(Boolean),
    },
  ].filter((g) => g.items.length > 0);

  const DEFAULT_TAB = searchParams.get("tab") || (canManageUsers ? "users" : "master");
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  // Mobile: true = menampilkan daftar grup (halaman utama Pengaturan), false = menampilkan
  // detail 1 tab dengan tombol kembali (mirip iOS Settings). Kalau datang dari link dengan
  // ?tab=..., langsung buka detailnya, bukan daftar.
  const [mobileShowList, setMobileShowList] = useState(!searchParams.get("tab"));
  const activeSettingsLabel = SETTINGS_GROUPS.flatMap((g) => g.items).find((i) => i.value === activeTab)?.label || "Pengaturan";

  const { branches, master, companyAddresses, companySignatures, bankAccounts, companyLogoPath, quotationTemplate, documentTypes: customDocTypes } = useMeta();
  const { data: users = [] } = useQuery({ queryKey: ["users-meta"], queryFn: async () => (await api.get("/users")).data });
  const { data: waTemplates = [] } = useQuery({ queryKey: ["wa-templates"], queryFn: async () => (await api.get("/wa-templates")).data });
  const [waForm, setWaForm] = useState({ code: "", title: "", message: "" });
  const [waEditId, setWaEditId] = useState(null);
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const [userOpen, setUserOpen] = useState(false);
  const [uform, setUform] = useState({ email: "", name: "", role: "sales", branch_id: "", managed_branch_id: "", password: "" });
  const ALL_ROLE_OPTIONS = [
    { value: "sales", label: "Sales" },
    { value: "manager", label: "Manager" },
    { value: "admin", label: "Admin" },
    { value: "chief", label: "Chief" },
    { value: "owner", label: "Owner" },
  ];
  const roleOptions = isOwnerOrChief ? ALL_ROLE_OPTIONS : ALL_ROLE_OPTIONS.filter((r) => !["owner", "chief"].includes(r.value));
  const [branchName, setBranchName] = useState("");
  const [masterType, setMasterType] = useState("sumber_info");
  const [masterVal, setMasterVal] = useState("");
  const { data: branding } = useQuery({ queryKey: ["branding"], queryFn: async () => (await api.get("/settings/branding")).data });
  const [companyName, setCompanyName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);
  React.useEffect(() => { if (branding?.company_name) setCompanyName(branding.company_name); }, [branding]);
  React.useEffect(() => { if (branding && !contactEmail) setContactEmail(branding.contact_email || ""); }, [branding]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveBranding = async () => {
    if (!companyName.trim()) { toast.error("Nama perusahaan tidak boleh kosong"); return; }
    setSavingBrand(true);
    try {
      await api.put("/settings/branding", { company_name: companyName.trim(), contact_email: contactEmail.trim() });
      toast.success("Branding disimpan");
      qc.invalidateQueries({ queryKey: ["branding"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingBrand(false); }
  };

  const { data: reportSchedule } = useQuery({ queryKey: ["report-schedule"], queryFn: async () => (await api.get("/settings/report-schedule")).data, enabled: isAdminOrOwner });
  const [scheduleForm, setScheduleForm] = useState(null);
  const sf = scheduleForm || reportSchedule || { enabled: true, interval_days: 7, day_of_week: 5, hour: 8, minute: 0, reports: { omset: true, rekap_client: true } };
  const REPORT_MODULE_OPTIONS = [
    { key: "omset", label: "Rekap Omset (PDF + Excel)" },
    { key: "rekap_client", label: "Rekap Client (PDF)" },
  ];
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const DOW_OPTIONS = [
    { value: 0, label: "Senin" }, { value: 1, label: "Selasa" }, { value: 2, label: "Rabu" },
    { value: 3, label: "Kamis" }, { value: 4, label: "Jumat" }, { value: 5, label: "Sabtu" }, { value: 6, label: "Minggu" },
  ];
  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const r = await api.put("/settings/report-schedule", {
        enabled: sf.enabled, interval_days: Number(sf.interval_days) || 7,
        day_of_week: Number(sf.day_of_week), hour: Number(sf.hour), minute: Number(sf.minute),
        reports: sf.reports || { omset: true, rekap_client: true },
      });
      notifyResult(r.data, "Jadwal laporan disimpan");
      setScheduleForm(null);
      qc.invalidateQueries({ queryKey: ["report-schedule"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingSchedule(false); }
  };
  const [togglingSchedule, setTogglingSchedule] = useState(false);
  // Guards against a duplicate event firing on a single tap (seen on mobile touch
  // emulation: touchend + a synthesized click both reaching this handler). A React
  // state check alone isn't enough — state only updates on the next render, so a
  // second synchronous dispatch in the same tick would still read the old value and
  // re-flip the toggle right back. This ref is set immediately, no render needed.
  const togglingLockRef = useRef(false);
  const toggleScheduleEnabled = async (next) => {
    if (togglingLockRef.current) return;
    togglingLockRef.current = true;
    setTogglingSchedule(true);
    try {
      const r = await api.put("/settings/report-schedule", {
        enabled: next, interval_days: Number(sf.interval_days) || 7,
        day_of_week: Number(sf.day_of_week), hour: Number(sf.hour), minute: Number(sf.minute),
        reports: sf.reports || { omset: true, rekap_client: true },
      });
      setScheduleForm(null);
      qc.invalidateQueries({ queryKey: ["report-schedule"] });
      notifyResult(r.data, next ? "Laporan terjadwal diaktifkan" : "Laporan terjadwal dinonaktifkan");
    } catch (e) { toast.error(apiErr(e)); } finally { setTogglingSchedule(false); togglingLockRef.current = false; }
  };
  const sendReportNow = async () => {
    setSendingNow(true);
    try {
      const r = await api.post("/reports/weekly-report/send-now");
      toast.success(`Laporan terkirim ke ${r.data.recipients?.length || 0} penerima`);
      qc.invalidateQueries({ queryKey: ["report-schedule"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setSendingNow(false); }
  };

  const inval = () => qc.invalidateQueries();

  // Approval-gated writes (sensitive company data / user management) return
  // {pending:true, message} instead of the usual saved record when the actor
  // is Admin — surface that distinctly so it's clear nothing went live yet.
  const notifyResult = (data, fallbackMsg) => {
    if (data?.pending) { toast.info(data.message || "Menunggu persetujuan Owner/Chief."); return true; }
    toast.success(fallbackMsg);
    return false;
  };

  // Logo perusahaan
  const logoRef = useRef();
  const [logoUploading, setLogoUploading] = useState(false);
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setLogoUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload?public=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.put("/settings/company-logo", { logo_path: data.path });
      notifyResult(res.data, "Logo perusahaan disimpan"); qc.invalidateQueries();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setLogoUploading(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  // Alamat perusahaan
  const blankAddress = { label: "", address: "", phone: "", email: "" };
  const [addrForm, setAddrForm] = useState(blankAddress);
  const [addrEditId, setAddrEditId] = useState(null);
  const saveAddr = async () => {
    if (!addrForm.label.trim()) { toast.error("Isi Nama/Label alamat"); return; }
    try {
      const { data } = addrEditId ? await api.put(`/company-addresses/${addrEditId}`, addrForm) : await api.post("/company-addresses", addrForm);
      notifyResult(data, "Alamat disimpan"); setAddrForm(blankAddress); setAddrEditId(null);
      qc.invalidateQueries();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const editAddr = (a) => { setAddrForm({ label: a.label, address: a.address, phone: a.phone, email: a.email }); setAddrEditId(a.address_id); };
  const cancelAddr = () => { setAddrForm(blankAddress); setAddrEditId(null); };
  const delAddr = async (id) => {
    if (!window.confirm("Hapus alamat ini?")) return;
    try { const { data } = await api.delete(`/company-addresses/${id}`); notifyResult(data, "Alamat dihapus"); qc.invalidateQueries(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // TTD / Cap / Materai
  const sigRef = useRef();
  const [sigUploading, setSigUploading] = useState(false);
  const [sigForm, setSigForm] = useState({ label: "", kind: "ttd", title: "" });
  const uploadSignature = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!sigForm.label.trim()) { toast.error("Isi Nama/Label dulu sebelum upload gambar"); if (sigRef.current) sigRef.current.value = ""; return; }
    setSigUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload?public=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post("/company-signatures", { ...sigForm, image_path: data.path });
      notifyResult(res.data, "Tersimpan"); setSigForm({ label: "", kind: "ttd", title: "" });
      qc.invalidateQueries();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSigUploading(false); if (sigRef.current) sigRef.current.value = ""; }
  };
  const delSig = async (id) => {
    if (!window.confirm("Hapus TTD/Cap/Materai ini?")) return;
    try { const { data } = await api.delete(`/company-signatures/${id}`); notifyResult(data, "Dihapus"); qc.invalidateQueries(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // Rekening bank
  const blankBank = { label: "", bank: "", acc_no: "", acc_name: "" };
  const [bankForm, setBankForm] = useState(blankBank);
  const [bankEditId, setBankEditId] = useState(null);
  const saveBank = async () => {
    if (!bankForm.label.trim()) { toast.error("Isi Nama/Label rekening"); return; }
    try {
      const { data } = bankEditId ? await api.put(`/company-bank-accounts/${bankEditId}`, bankForm) : await api.post("/company-bank-accounts", bankForm);
      notifyResult(data, "Rekening disimpan"); setBankForm(blankBank); setBankEditId(null);
      qc.invalidateQueries();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const editBank = (b) => { setBankForm({ label: b.label, bank: b.bank, acc_no: b.acc_no, acc_name: b.acc_name }); setBankEditId(b.bank_account_id); };
  const cancelBank = () => { setBankForm(blankBank); setBankEditId(null); };
  const delBank = async (id) => {
    if (!window.confirm("Hapus rekening ini?")) return;
    try { const { data } = await api.delete(`/company-bank-accounts/${id}`); notifyResult(data, "Rekening dihapus"); qc.invalidateQueries(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // Folder Dokumen Template: SPH (naskah editable, existing), Invoice (belum ada naskah
  // terpisah — field per-dokumen sudah cukup), Kontrak (belum ada sama sekali, placeholder).
  const [templateView, setTemplateView] = useState("cards");
  // Template naskah SPH/Quotation — bisa diedit bebas, boleh dikosongkan
  const [tplForm, setTplForm] = useState(null);
  const [savingTpl, setSavingTpl] = useState(false);
  React.useEffect(() => { if (quotationTemplate && !tplForm) setTplForm(quotationTemplate); }, [quotationTemplate, tplForm]);
  const saveTemplate = async () => {
    setSavingTpl(true);
    try { await api.put("/settings/quotation-template", tplForm); toast.success("Template SPH disimpan"); qc.invalidateQueries({ queryKey: ["quotation-template"] }); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingTpl(false); }
  };

  // Flexible sections (Ruang Lingkup / Revisi / dst.) — free-form list, addable /
  // removable / renamable. Only sets the DEFAULT that new documents start from —
  // editing here never changes documents that already exist (each snapshots its own
  // copy at creation, see server.py create_document).
  const { data: defaultSections } = useQuery({ queryKey: ["quotation-sections"], queryFn: async () => (await api.get("/settings/quotation-sections")).data });
  const [sectionsForm, setSectionsForm] = useState(null);
  const [savingSections, setSavingSections] = useState(false);
  React.useEffect(() => { if (defaultSections && !sectionsForm) setSectionsForm(defaultSections); }, [defaultSections, sectionsForm]);
  const newSectionId = () => `sec_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const addSection = () => setSectionsForm((s) => [...s, { id: newSectionId(), title: "Bagian Baru", body: "" }]);
  const removeSection = (idx) => setSectionsForm((s) => s.filter((_, i) => i !== idx));
  const moveSection = (idx, dir) => setSectionsForm((s) => {
    const next = [...s]; const j = idx + dir;
    if (j < 0 || j >= next.length) return s;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const saveSections = async () => {
    setSavingSections(true);
    try { await api.put("/settings/quotation-sections", sectionsForm); toast.success("Bagian dokumen disimpan — hanya berlaku untuk dokumen baru"); qc.invalidateQueries({ queryKey: ["quotation-sections"] }); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingSections(false); }
  };

  // Kontrak Design — identical mechanism to the SPH sections above, but two variants
  // ("full" goes to Shop Drawing/RAB, "3d" stops at Rendering Final) each under their
  // own settings key/query, switched via a tab so both stay independently editable.
  const [kontrakVariant, setKontrakVariant] = useState("full"); // "full" | "3d"
  const { data: defaultKontrakSections } = useQuery({ queryKey: ["kontrak-design-sections", kontrakVariant], queryFn: async () => (await api.get(`/settings/kontrak-design-sections?variant=${kontrakVariant}`)).data });
  const [kontrakSectionsForm, setKontrakSectionsForm] = useState(null);
  const [savingKontrakSections, setSavingKontrakSections] = useState(false);
  React.useEffect(() => { setKontrakSectionsForm(null); }, [kontrakVariant]);
  React.useEffect(() => { if (defaultKontrakSections && !kontrakSectionsForm) setKontrakSectionsForm(defaultKontrakSections); }, [defaultKontrakSections, kontrakSectionsForm]);
  const addKontrakSection = () => setKontrakSectionsForm((s) => [...s, { id: newSectionId(), title: "Pasal Baru", body: "", kind: "text" }]);
  const removeKontrakSection = (idx) => setKontrakSectionsForm((s) => s.filter((_, i) => i !== idx));
  const moveKontrakSection = (idx, dir) => setKontrakSectionsForm((s) => {
    const next = [...s]; const j = idx + dir;
    if (j < 0 || j >= next.length) return s;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const saveKontrakSections = async () => {
    setSavingKontrakSections(true);
    try { await api.put(`/settings/kontrak-design-sections?variant=${kontrakVariant}`, kontrakSectionsForm); toast.success("Pasal kontrak disimpan — hanya berlaku untuk kontrak baru"); qc.invalidateQueries({ queryKey: ["kontrak-design-sections"] }); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingKontrakSections(false); }
  };

  // Kontrak Produksi — same mechanism again, single variant (17-Pasal SPK Produksi).
  const { data: defaultProduksiSections } = useQuery({ queryKey: ["kontrak-produksi-sections"], queryFn: async () => (await api.get("/settings/kontrak-produksi-sections")).data });
  const [produksiSectionsForm, setProduksiSectionsForm] = useState(null);
  const [savingProduksiSections, setSavingProduksiSections] = useState(false);
  React.useEffect(() => { if (defaultProduksiSections && !produksiSectionsForm) setProduksiSectionsForm(defaultProduksiSections); }, [defaultProduksiSections, produksiSectionsForm]);
  const addProduksiSection = () => setProduksiSectionsForm((s) => [...s, { id: newSectionId(), title: "Pasal Baru", body: "", kind: "text" }]);
  const removeProduksiSection = (idx) => setProduksiSectionsForm((s) => s.filter((_, i) => i !== idx));
  const moveProduksiSection = (idx, dir) => setProduksiSectionsForm((s) => {
    const next = [...s]; const j = idx + dir;
    if (j < 0 || j >= next.length) return s;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const saveProduksiSections = async () => {
    setSavingProduksiSections(true);
    try { await api.put("/settings/kontrak-produksi-sections", produksiSectionsForm); toast.success("Pasal kontrak disimpan — hanya berlaku untuk kontrak baru"); qc.invalidateQueries({ queryKey: ["kontrak-produksi-sections"] }); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingProduksiSections(false); }
  };
  const PRODUKSI_AUTO_KINDS = ["biaya_termin", "jangka_waktu", "nilai_ppn", "garansi", "lingkup_produksi"];

  // Custom document types — a builder for whole new document CATEGORIES (not a
  // single freeform document). Naming and saving one here turns it into its own
  // folder (dynamically listed in the cards grid) AND its own selectable "Jenis"
  // in DocumentDialog — same flexible-sections mechanism as SPH/Kontrak Design.
  const blankCustomSection = () => [{ id: newSectionId(), title: "Isi Dokumen", body: "", kind: "text" }];
  const [customBuilderName, setCustomBuilderName] = useState("");
  const [customBuilderSections, setCustomBuilderSections] = useState(blankCustomSection());
  const [savingCustomType, setSavingCustomType] = useState(false);
  const addCustomBuilderSection = () => setCustomBuilderSections((s) => [...s, { id: newSectionId(), title: "Bagian Baru", body: "", kind: "text" }]);
  const removeCustomBuilderSection = (idx) => setCustomBuilderSections((s) => s.filter((_, i) => i !== idx));
  const moveCustomBuilderSection = (idx, dir) => setCustomBuilderSections((s) => {
    const next = [...s]; const j = idx + dir;
    if (j < 0 || j >= next.length) return s;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const saveNewCustomType = async () => {
    if (!customBuilderName.trim()) { toast.error("Nama kategori dokumen wajib diisi"); return; }
    setSavingCustomType(true);
    try {
      const { data } = await api.post("/document-types", { name: customBuilderName.trim(), sections: customBuilderSections });
      toast.success(`Folder "${data.name}" dibuat`);
      setCustomBuilderName("");
      setCustomBuilderSections(blankCustomSection());
      qc.invalidateQueries({ queryKey: ["document-types"] });
      setTemplateView("cards");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingCustomType(false); }
  };

  // Editing an existing custom type's default sections/name (still flexible,
  // still never touches documents already created from it — same snapshot rule).
  const [editCustomForm, setEditCustomForm] = useState(null);
  const [savingEditCustomType, setSavingEditCustomType] = useState(false);
  React.useEffect(() => {
    if (templateView.startsWith("custom:") && !editCustomForm) {
      const found = customDocTypes.find((t) => t.type_id === templateView.slice(7));
      if (found) setEditCustomForm({ name: found.name, sections: found.sections });
    }
  }, [templateView, editCustomForm, customDocTypes]);
  const saveEditCustomType = async (typeId) => {
    setSavingEditCustomType(true);
    try {
      await api.put(`/document-types/${typeId}`, editCustomForm);
      toast.success("Kategori dokumen disimpan — hanya berlaku untuk dokumen baru");
      qc.invalidateQueries({ queryKey: ["document-types"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingEditCustomType(false); }
  };
  const [deletingCustomType, setDeletingCustomType] = useState(false);
  const deleteCustomType = async (typeId, name) => {
    if (!window.confirm(`Hapus kategori dokumen "${name}"? Dokumen yang sudah pernah dibuat dari kategori ini tidak akan terhapus/terganggu — ini cuma menghapus foldernya dari pilihan "Jenis" saat membuat dokumen baru.`)) return;
    setDeletingCustomType(true);
    try {
      const { data } = await api.delete(`/document-types/${typeId}`);
      notifyResult(data, "Kategori dokumen dihapus");
      qc.invalidateQueries({ queryKey: ["document-types"] });
      setTemplateView("cards");
      setEditCustomForm(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setDeletingCustomType(false); }
  };

  const addUser = async () => {
    try {
      const { data } = await api.post("/users", { ...uform, branch_id: uform.branch_id || null, managed_branch_id: uform.managed_branch_id || null });
      notifyResult(data, "User dibuat"); inval(); setUserOpen(false);
      setUform({ email: "", name: "", role: "sales", branch_id: "", managed_branch_id: "", password: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delUser = async (id) => {
    if (!window.confirm("Hapus user?")) return;
    try { const { data } = await api.delete(`/users/${id}`); notifyResult(data, "Dihapus"); inval(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addBranch = async () => {
    if (!branchName.trim()) return;
    try { const { data } = await api.post("/branches", { name: branchName }); setBranchName(""); notifyResult(data, "Cabang ditambah"); inval(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delBranch = async (id) => {
    try { const { data } = await api.delete(`/branches/${id}`); notifyResult(data, "Cabang dihapus"); inval(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addMaster = async () => { if (!masterVal.trim()) return; try { await api.post("/master", { type: masterType, value: masterVal }); setMasterVal(""); toast.success("Ditambah"); inval(); } catch (e) { toast.error(apiErr(e)); } };
  const delMaster = async (id) => { try { await api.delete(`/master/${id}`); inval(); } catch (e) { toast.error(apiErr(e)); } };
  const addWa = async () => {
    if (!waForm.code.trim() || !waForm.message.trim()) { toast.error("Isi kode & pesan"); return; }
    try {
      if (waEditId) { await api.put(`/wa-templates/${waEditId}`, waForm); toast.success("Template diperbarui"); }
      else { await api.post("/wa-templates", waForm); toast.success("Template disimpan"); }
      setWaForm({ code: "", title: "", message: "" }); setWaEditId(null); inval();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const editWa = (t) => { setWaForm({ code: t.code, title: t.title, message: t.message }); setWaEditId(t.tpl_id); };
  const cancelWa = () => { setWaForm({ code: "", title: "", message: "" }); setWaEditId(null); };
  const delWa = async (id) => { try { await api.delete(`/wa-templates/${id}`); inval(); } catch (e) { toast.error(apiErr(e)); } };

  const [importResult, setImportResult] = useState(null);
  const doImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setImportResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/leads/import/excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import selesai: ${data.imported} masuk, ${data.skipped} duplikat di-skip`);
      setImportResult(data); inval();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doExport = () => {
    const token = localStorage.getItem("crm_token");
    fetch(`${API}/leads/export/excel`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = "leads_export.xlsx"; a.click(); URL.revokeObjectURL(url);
      }).catch(() => toast.error("Gagal export"));
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("crm_token");
    fetch(`${API}/leads/import/template`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = "template_import_leads.xlsx"; a.click(); URL.revokeObjectURL(url);
      }).catch(() => toast.error("Gagal download template"));
  };

  const MASTER_TYPES = ["sumber_info", "kategori_customer", "layanan", "kota", "gender", "kelompok_umur", "alasan_lost", "metode_bayar"];

  const selectTab = (v) => { setActiveTab(v); setMobileShowList(false); };

  return (
    <div className="px-4 py-5 max-w-6xl mx-auto md:px-8">
      {/* Mobile: header berubah sesuai mode — daftar grup vs. detail 1 tab dengan tombol kembali */}
      <div className="md:hidden mb-4">
        {mobileShowList ? (
          <h1 className="text-xl font-bold font-heading tracking-tight">Pengaturan</h1>
        ) : (
          <button data-testid="settings-back" onClick={() => setMobileShowList(true)}
            className="flex items-center gap-1 text-blue-400 text-sm font-medium -ml-1 px-1 py-1">
            <ChevronLeft size={18} />Pengaturan
          </button>
        )}
      </div>
      <h1 className="hidden md:block text-xl font-bold font-heading tracking-tight mb-5">Pengaturan</h1>

      <Tabs value={activeTab} onValueChange={selectTab} className="md:grid md:grid-cols-[240px_1fr] md:gap-6 md:items-start">
        {/* Sidebar desktop, ala Coinbase: grup dengan label kecil uppercase, item full-width */}
        <nav className="hidden md:block sticky top-4 space-y-5">
          {SETTINGS_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 px-2.5 mb-1.5">{g.title}</div>
              <div className="space-y-0.5">
                {g.items.map((it) => {
                  const Icon = it.icon;
                  const active = activeTab === it.value;
                  return (
                    <button key={it.value} data-testid={`settings-nav-${it.value}`} onClick={() => selectTab(it.value)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13.5px] font-medium transition-colors ${
                        active ? "bg-blue-500/15 text-blue-300" : "text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
                      }`}>
                      <Icon size={16} className="shrink-0" />{it.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Daftar grup mobile, ala iOS Settings: kartu bulat per grup, chevron di kanan */}
        {mobileShowList && (
          <div className="md:hidden space-y-5">
            {SETTINGS_GROUPS.map((g) => (
              <div key={g.title}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 px-1 mb-1.5">{g.title}</div>
                <div className="card-surface rounded-xl overflow-hidden divide-y divide-white/5">
                  {g.items.map((it) => {
                    const Icon = it.icon;
                    return (
                      <button key={it.value} data-testid={`settings-nav-mobile-${it.value}`} onClick={() => selectTab(it.value)}
                        className="w-full flex items-center gap-3 px-3.5 py-3 text-left active:bg-white/5">
                        <Icon size={17} className="shrink-0 text-zinc-400" />
                        <span className="flex-1 text-[14px] font-medium">{it.label}</span>
                        <ChevronRight size={16} className="text-zinc-600 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className={mobileShowList ? "hidden md:block md:min-w-0" : "md:min-w-0"}>
        {isAdminOrOwner && <TabsContent value="branding" className="mt-4">
          <div className="card-surface rounded-2xl p-5 max-w-md">
            <h3 className="font-heading font-semibold mb-1">Nama Perusahaan</h3>
            <p className="text-sm text-zinc-500 mb-4">Nama aplikasi "Equity CRM" sudah baku. Nama perusahaan di bawah ini tampil sebagai sub-judul di halaman login & header, sesuaikan dengan bisnis kamu.</p>
            <Label className="text-[11px] text-zinc-400 uppercase">Nama Perusahaan</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="company-name-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="mis. Adari.interior" className="bg-[#18181B] border-white/10 h-10" />
            </div>
            <div className="mt-4">
              <Label className="text-[11px] text-zinc-400 uppercase">Email Kontak (Reply-To)</Label>
              <p className="text-xs text-zinc-600 mt-0.5 mb-1.5">Alamat pengirim email notifikasi tetap dari domain Equity CRM (supaya tidak dianggap spam) — tapi kalau klien membalas email, balasannya langsung masuk ke email ini.</p>
              <Input data-testid="contact-email-input" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="mis. info@adariinterior.com" className="bg-[#18181B] border-white/10 h-10" />
            </div>
            <Button data-testid="company-name-save" onClick={saveBranding} disabled={savingBrand} className="h-10 bg-blue-700 hover:bg-blue-800 mt-4 w-full">
              {savingBrand ? <Loader2 size={16} className="animate-spin" /> : "Simpan Branding"}
            </Button>
          </div>
        </TabsContent>}

        {isAdminOrOwner && <TabsContent value="doc-logo" className="mt-4">
          <div className="card-surface rounded-2xl p-5 max-w-md">
            <h3 className="font-heading font-semibold mb-1">Logo Perusahaan</h3>
            <p className="text-sm text-zinc-500 mb-4">Logo ini beda dari logo aplikasi Equity CRM — ini logo bisnis kamu sendiri (mis. Adari Interior), otomatis dipakai di header semua Invoice/SPH.</p>
            <div className="flex items-center gap-4">
              <div className="w-28 h-28 rounded-xl bg-[#18181B] border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                {companyLogoPath ? <img src={fileUrl(companyLogoPath)} alt="logo" className="w-full h-full object-contain" /> : <ImageIcon size={28} className="text-zinc-600" />}
              </div>
              <div>
                <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} className="hidden" data-testid="logo-file" />
                <Button onClick={() => logoRef.current?.click()} disabled={logoUploading} className="bg-blue-600 h-10">
                  {logoUploading ? <Loader2 className="animate-spin" size={16} /> : <><Upload size={16} className="mr-1.5" />{companyLogoPath ? "Ganti Logo" : "Upload Logo"}</>}
                </Button>
                {!companyLogoPath && <p className="text-[11px] text-amber-400 mt-2">Belum ada logo — dokumen akan tampil dengan placeholder.</p>}
              </div>
            </div>
          </div>
        </TabsContent>}

        {isAdminOrOwner && <TabsContent value="doc-address" className="mt-4">
          <div className="card-surface rounded-2xl p-5 mb-3">
            <h3 className="font-heading font-semibold mb-3">{addrEditId ? "Edit Alamat" : "+ Tambah Alamat"}</h3>
            <div className="grid gap-2">
              <Input value={addrForm.label} onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} placeholder="Nama/Label (mis. Kantor Pusat)" className="bg-[#18181B] border-white/10 h-10" />
              <textarea value={addrForm.address} onChange={(e) => setAddrForm({ ...addrForm, address: e.target.value })} placeholder="Alamat lengkap" rows={2} className="bg-[#18181B] border border-white/10 rounded-lg p-2.5 text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <Input value={addrForm.phone} onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value })} placeholder="Telepon" className="bg-[#18181B] border-white/10 h-10" />
                <Input value={addrForm.email} onChange={(e) => setAddrForm({ ...addrForm, email: e.target.value })} placeholder="Email" className="bg-[#18181B] border-white/10 h-10" />
              </div>
              <div className="flex gap-2">
                <Button onClick={saveAddr} className="bg-blue-600 h-10"><Plus size={16} className="mr-1" />{addrEditId ? "Update Alamat" : "Simpan Alamat"}</Button>
                {addrEditId && <Button onClick={cancelAddr} variant="outline" className="bg-[#18181B] border-white/10 h-10">Batal</Button>}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {companyAddresses.map((a) => (
              <div key={a.address_id} className="card-surface rounded-xl p-3.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{a.label}</div>
                  <p className="text-xs text-zinc-500 mt-0.5">{a.address}</p>
                  <p className="text-xs text-zinc-600 mt-0.5">{a.phone} {a.email && `· ${a.email}`}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => editAddr(a)} className="text-blue-300 p-1.5 hover:bg-white/5 rounded-lg"><Pencil size={14} /></button>
                  <button onClick={() => delAddr(a.address_id)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {!companyAddresses.length && <p className="text-sm text-zinc-500">Belum ada alamat tersimpan.</p>}
          </div>
        </TabsContent>}

        {isAdminOrOwner && <TabsContent value="doc-sign" className="mt-4">
          <div className="card-surface rounded-2xl p-5 mb-3">
            <h3 className="font-heading font-semibold mb-3">+ Tambah TTD / Cap / Materai</h3>
            <div className="grid gap-2">
              <Input value={sigForm.label} onChange={(e) => setSigForm({ ...sigForm, label: e.target.value })} placeholder="Nama/Label (mis. TTD Manager Keuangan)" className="bg-[#18181B] border-white/10 h-10" />
              <div className="grid grid-cols-2 gap-2">
                <Select value={sigForm.kind} onValueChange={(v) => setSigForm({ ...sigForm, kind: v })}>
                  <SelectTrigger className="bg-[#18181B] border-white/10 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10">
                    <SelectItem value="ttd">Tanda Tangan</SelectItem><SelectItem value="cap">Cap Perusahaan</SelectItem><SelectItem value="materai">Materai Digital</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={sigForm.title} onChange={(e) => setSigForm({ ...sigForm, title: e.target.value })} placeholder="Jabatan (opsional)" className="bg-[#18181B] border-white/10 h-10" />
              </div>
              <input ref={sigRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadSignature} className="hidden" data-testid="signature-file" />
              <Button onClick={() => sigRef.current?.click()} disabled={sigUploading} className="bg-blue-600 h-10 w-fit">
                {sigUploading ? <Loader2 className="animate-spin" size={16} /> : <><Upload size={16} className="mr-1.5" />Upload Gambar</>}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {companySignatures.map((s) => (
              <div key={s.signature_id} className="card-surface rounded-xl p-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-16 h-12 rounded-lg bg-[#18181B] border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                    {s.image_path ? <img src={fileUrl(s.image_path)} alt={s.label} className="w-full h-full object-contain" /> : <ImageIcon size={16} className="text-zinc-600" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{s.label} <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 ml-1 capitalize">{s.kind}</span></div>
                    {s.title && <div className="text-xs text-zinc-500">{s.title}</div>}
                  </div>
                </div>
                <button onClick={() => delSig(s.signature_id)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg shrink-0"><Trash2 size={14} /></button>
              </div>
            ))}
            {!companySignatures.length && <p className="text-sm text-zinc-500">Belum ada TTD/Cap/Materai tersimpan.</p>}
          </div>
        </TabsContent>}

        {isAdminOrOwner && <TabsContent value="doc-bank" className="mt-4">
          <div className="card-surface rounded-2xl p-5 mb-3">
            <h3 className="font-heading font-semibold mb-3">{bankEditId ? "Edit Rekening" : "+ Tambah Rekening"}</h3>
            <div className="grid gap-2">
              <Input value={bankForm.label} onChange={(e) => setBankForm({ ...bankForm, label: e.target.value })} placeholder="Nama/Label (mis. Rekening Utama)" className="bg-[#18181B] border-white/10 h-10" />
              <div className="grid grid-cols-2 gap-2">
                <Input value={bankForm.bank} onChange={(e) => setBankForm({ ...bankForm, bank: e.target.value })} placeholder="Nama Bank" className="bg-[#18181B] border-white/10 h-10" />
                <Input value={bankForm.acc_no} onChange={(e) => setBankForm({ ...bankForm, acc_no: e.target.value })} placeholder="Nomor Rekening" className="bg-[#18181B] border-white/10 h-10" />
              </div>
              <Input value={bankForm.acc_name} onChange={(e) => setBankForm({ ...bankForm, acc_name: e.target.value })} placeholder="Atas Nama" className="bg-[#18181B] border-white/10 h-10" />
              <div className="flex gap-2">
                <Button onClick={saveBank} className="bg-blue-600 h-10"><Plus size={16} className="mr-1" />{bankEditId ? "Update Rekening" : "Simpan Rekening"}</Button>
                {bankEditId && <Button onClick={cancelBank} variant="outline" className="bg-[#18181B] border-white/10 h-10">Batal</Button>}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {bankAccounts.map((b) => (
              <div key={b.bank_account_id} className="card-surface rounded-xl p-3.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{b.label}</div>
                  <p className="text-xs text-zinc-500 mt-0.5">{b.bank} · {b.acc_no}</p>
                  <p className="text-xs text-zinc-600 mt-0.5">a.n. {b.acc_name}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => editBank(b)} className="text-blue-300 p-1.5 hover:bg-white/5 rounded-lg"><Pencil size={14} /></button>
                  <button onClick={() => delBank(b.bank_account_id)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {!bankAccounts.length && <p className="text-sm text-zinc-500">Belum ada rekening tersimpan.</p>}
          </div>
        </TabsContent>}

        {isAdminOrOwner && <TabsContent value="doc-sph" className="mt-4">
          {templateView === "cards" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button onClick={() => setTemplateView("sph")} className="card-surface rounded-2xl p-5 text-left hover:bg-white/5 transition-colors">
                <FileText className="text-blue-400 mb-2" size={22} />
                <h3 className="font-heading font-semibold">SPH / Quotation</h3>
                <p className="text-xs text-zinc-500 mt-1">Naskah surat penawaran — bisa diedit bebas.</p>
              </button>
              <button onClick={() => setTemplateView("invoice")} className="card-surface rounded-2xl p-5 text-left hover:bg-white/5 transition-colors">
                <Receipt className="text-emerald-400 mb-2" size={22} />
                <h3 className="font-heading font-semibold">Invoice</h3>
                <p className="text-xs text-zinc-500 mt-1">Info tentang pengaturan invoice.</p>
              </button>
              <button onClick={() => setTemplateView("kontrak-design")} className="card-surface rounded-2xl p-5 text-left hover:bg-white/5 transition-colors">
                <FileSignature className="text-amber-400 mb-2" size={22} />
                <h3 className="font-heading font-semibold">Kontrak Design</h3>
                <p className="text-xs text-zinc-500 mt-1">Naskah surat perjanjian jasa desain — bisa diedit bebas.</p>
              </button>
              <button onClick={() => setTemplateView("kontrak-produksi")} className="card-surface rounded-2xl p-5 text-left hover:bg-white/5 transition-colors">
                <FileSignature className="text-rose-400 mb-2" size={22} />
                <h3 className="font-heading font-semibold">Kontrak Produksi</h3>
                <p className="text-xs text-zinc-500 mt-1">17 Pasal SPK Produksi — kategori, PPN, termin, denda, garansi otomatis.</p>
              </button>
              {customDocTypes.map((t) => (
                <button key={t.type_id} onClick={() => setTemplateView(`custom:${t.type_id}`)} className="card-surface rounded-2xl p-5 text-left hover:bg-white/5 transition-colors">
                  <FileText className="text-purple-400 mb-2" size={22} />
                  <h3 className="font-heading font-semibold">{t.name}</h3>
                  <p className="text-xs text-zinc-500 mt-1">Kategori dokumen kustom — bisa diedit bebas.</p>
                </button>
              ))}
              <button onClick={() => setTemplateView("custom-new")} className="card-surface rounded-2xl p-5 text-left hover:bg-white/5 transition-colors border-dashed border-2 border-white/10">
                <Plus className="text-purple-400 mb-2" size={22} />
                <h3 className="font-heading font-semibold">Buat Kategori Dokumen Baru</h3>
                <p className="text-xs text-zinc-500 mt-1">Untuk kebutuhan dokumen yang belum ada jenisnya — beri nama, susun isinya, jadi folder sendiri.</p>
              </button>
            </div>
          )}

          {templateView === "sph" && (
            <div>
              <button onClick={() => setTemplateView("cards")} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 mb-3"><ChevronLeft size={14} /> Kembali ke Folder Dokumen</button>
              <div className="card-surface rounded-2xl p-5 mb-3">
                <h3 className="font-heading font-semibold mb-1">Naskah Surat Quotation / SPH</h3>
                <p className="text-sm text-zinc-500 mb-4">Ini isi surat SPH yang dicetak (di luar data client/item/harga yang otomatis dari dokumen). Edit bebas sesuai kebutuhan, atau kosongkan salah satu bagian kalau tidak ingin ditampilkan sama sekali di cetakan.</p>
                {!tplForm ? <Loader2 className="animate-spin text-zinc-500" size={18} /> : (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-[11px] text-zinc-400 uppercase">Perihal</Label>
                      <Input value={tplForm.perihal} onChange={(e) => setTplForm({ ...tplForm, perihal: e.target.value })} className="bg-[#18181B] border-white/10 h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-zinc-400 uppercase">Paragraf Pembuka (setelah "Dengan hormat,")</Label>
                      <textarea value={tplForm.intro} onChange={(e) => setTplForm({ ...tplForm, intro: e.target.value })} rows={6} className="w-full bg-[#18181B] border border-white/10 rounded-lg p-2.5 text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-zinc-400 uppercase">Paragraf Penutup (sebelum "Hormat Kami,")</Label>
                      <textarea value={tplForm.closing} onChange={(e) => setTplForm({ ...tplForm, closing: e.target.value })} rows={3} className="w-full bg-[#18181B] border border-white/10 rounded-lg p-2.5 text-sm mt-1" />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={saveTemplate} disabled={savingTpl} className="bg-blue-600 h-10">{savingTpl ? <Loader2 className="animate-spin" size={16} /> : "Simpan Template"}</Button>
                      <Button onClick={() => setTplForm(quotationTemplate)} variant="outline" className="bg-[#18181B] border-white/10 h-10">Batal</Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="card-surface rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-1">Bagian Dokumen (Ruang Lingkup, Revisi, dll.)</h3>
                <p className="text-sm text-zinc-500 mb-4">Bagian bebas — tambah, hapus, ganti nama, atau susun ulang urutannya sesuka Anda. Ini cuma jadi <b>default awal</b> untuk SPH baru — dokumen yang sudah pernah dibuat tidak pernah berubah walau bagian ini diedit lagi nanti, dan tiap SPH tetap bisa dikustomisasi sendiri-sendiri saat dibuat.</p>
                {!sectionsForm ? <Loader2 className="animate-spin text-zinc-500" size={18} /> : (
                  <div className="space-y-3">
                    {sectionsForm.map((s, i) => (
                      <div key={s.id || i} className="rounded-xl bg-[#18181B] border border-white/5 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-zinc-500 w-5 text-center shrink-0">{i + 1}.</span>
                          <Input value={s.title} onChange={(e) => setSectionsForm((arr) => arr.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                            placeholder="Judul bagian" className="bg-[#0f0f12] border-white/10 h-9 flex-1" />
                          <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="rotate-90" /></button>
                          <button onClick={() => moveSection(i, 1)} disabled={i === sectionsForm.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="-rotate-90" /></button>
                          <button onClick={() => removeSection(i)} className="text-red-400 hover:bg-red-500/10 rounded-lg p-1.5"><Trash2 size={15} /></button>
                        </div>
                        <textarea value={s.body} onChange={(e) => setSectionsForm((arr) => arr.map((x, j) => j === i ? { ...x, body: e.target.value } : x))}
                          rows={6} placeholder="Isi paragraf…" className="w-full bg-[#0f0f12] border border-white/10 rounded-lg p-2.5 text-sm" />
                      </div>
                    ))}
                    {sectionsForm.length === 0 && <p className="text-xs text-zinc-600 italic text-center py-4">Belum ada bagian. Tambah minimal 1 kalau ingin ada isi selain paragraf pembuka/penutup.</p>}
                    <Button onClick={addSection} variant="outline" className="w-full bg-[#18181B] border-white/10 h-9 text-sm"><Plus size={14} className="mr-1.5" />Tambah Bagian</Button>
                    <div className="flex gap-2 pt-1">
                      <Button onClick={saveSections} disabled={savingSections} className="bg-blue-600 h-10">{savingSections ? <Loader2 className="animate-spin" size={16} /> : "Simpan Bagian"}</Button>
                      <Button onClick={() => setSectionsForm(defaultSections)} variant="outline" className="bg-[#18181B] border-white/10 h-10">Batal</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {templateView === "invoice" && (
            <div>
              <button onClick={() => setTemplateView("cards")} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 mb-3"><ChevronLeft size={14} /> Kembali ke Folder Dokumen</button>
              <div className="card-surface rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-1">Invoice</h3>
                <p className="text-sm text-zinc-500">Invoice tidak pakai naskah baku terpisah seperti SPH — nomor, item, syarat pembayaran, dan penandatangan diisi langsung saat membuat dokumen invoice per client (lewat halaman Leads → Dokumen).</p>
              </div>
            </div>
          )}

          {templateView === "kontrak-design" && (
            <div>
              <button onClick={() => setTemplateView("cards")} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 mb-3"><ChevronLeft size={14} /> Kembali ke Folder Dokumen</button>
              <div className="card-surface rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-1">Pasal Kontrak Design</h3>
                <p className="text-sm text-zinc-500 mb-3">Pasal-pasal perjanjian jasa desain — tambah, hapus, ganti nama, atau susun ulang bebas. Pasal <b>"Biaya dan Cara Pembayaran"</b> otomatis terisi dari Item &amp; Termin dokumen, dan <b>"Jangka Waktu Pelaksanaan"</b> otomatis terisi dari field durasi di form pembuatan dokumen — keduanya tidak diketik manual di sini. Ini cuma jadi <b>default awal</b> untuk kontrak baru — kontrak yang sudah pernah dibuat tidak pernah berubah walau bagian ini diedit lagi nanti.</p>
                <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5 mb-4 w-fit">
                  <button onClick={() => setKontrakVariant("full")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${kontrakVariant === "full" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Full (s.d. RAB)</button>
                  <button onClick={() => setKontrakVariant("3d")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${kontrakVariant === "3d" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>3D Only (s.d. Rendering Final)</button>
                </div>
                {!kontrakSectionsForm ? <Loader2 className="animate-spin text-zinc-500" size={18} /> : (
                  <div className="space-y-3">
                    {kontrakSectionsForm.map((s, i) => (
                      <div key={s.id || i} className="rounded-xl bg-[#18181B] border border-white/5 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-zinc-500 w-16 shrink-0">Pasal {i + 1}</span>
                          <Input value={s.title} onChange={(e) => setKontrakSectionsForm((arr) => arr.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                            placeholder="Judul pasal" className="bg-[#0f0f12] border-white/10 h-9 flex-1" />
                          {(s.kind === "biaya" || s.kind === "jangka_waktu") && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30 shrink-0">Sebagian Otomatis</span>}
                          <button onClick={() => moveKontrakSection(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="rotate-90" /></button>
                          <button onClick={() => moveKontrakSection(i, 1)} disabled={i === kontrakSectionsForm.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="-rotate-90" /></button>
                          <button onClick={() => removeKontrakSection(i)} className="text-red-400 hover:bg-red-500/10 rounded-lg p-1.5"><Trash2 size={15} /></button>
                        </div>
                        {s.kind === "biaya" ? (
                          <p className="text-xs text-zinc-600 italic px-1">Isinya otomatis dari daftar Item &amp; Termin Pembayaran dokumen ini — tidak ada teks manual di sini.</p>
                        ) : (
                          <>
                            {s.kind === "jangka_waktu" && <p className="text-xs text-zinc-600 italic px-1 mb-1.5">Kalimat durasi ("selama X hari") otomatis dari field Jangka Waktu di form dokumen. Teks di bawah ini cuma bagian tambahan (syarat perpanjangan, dll).</p>}
                            <textarea value={s.body} onChange={(e) => setKontrakSectionsForm((arr) => arr.map((x, j) => j === i ? { ...x, body: e.target.value } : x))}
                              rows={6} placeholder="Isi pasal…" className="w-full bg-[#0f0f12] border border-white/10 rounded-lg p-2.5 text-sm" />
                          </>
                        )}
                      </div>
                    ))}
                    <Button onClick={addKontrakSection} variant="outline" className="w-full bg-[#18181B] border-white/10 h-9 text-sm"><Plus size={14} className="mr-1.5" />Tambah Pasal</Button>
                    <div className="flex gap-2 pt-1">
                      <Button onClick={saveKontrakSections} disabled={savingKontrakSections} className="bg-blue-600 h-10">{savingKontrakSections ? <Loader2 className="animate-spin" size={16} /> : "Simpan Pasal"}</Button>
                      <Button onClick={() => setKontrakSectionsForm(defaultKontrakSections)} variant="outline" className="bg-[#18181B] border-white/10 h-10">Batal</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {templateView === "kontrak-produksi" && (
            <div>
              <button onClick={() => setTemplateView("cards")} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 mb-3"><ChevronLeft size={14} /> Kembali ke Folder Dokumen</button>
              <div className="card-surface rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-1">Pasal Kontrak Produksi</h3>
                <p className="text-sm text-zinc-500 mb-4">17 Pasal SPK Pelaksanaan Produksi Interior — tambah, hapus, ganti nama, atau susun ulang bebas. Pasal <b>Nilai Kontrak</b>, <b>Termin Pembayaran</b>, <b>Jangka Waktu</b>, dan <b>Garansi</b> otomatis mengikuti isian di form pembuatan dokumen (Kategori Proyek, PPN, denda, dsb) — tidak diketik manual di sini. Ini cuma jadi <b>default awal</b> untuk kontrak baru — kontrak yang sudah pernah dibuat tidak pernah berubah walau bagian ini diedit lagi nanti.</p>
                {!produksiSectionsForm ? <Loader2 className="animate-spin text-zinc-500" size={18} /> : (
                  <div className="space-y-3">
                    {produksiSectionsForm.map((s, i) => (
                      <div key={s.id || i} className="rounded-xl bg-[#18181B] border border-white/5 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-zinc-500 w-5 text-center shrink-0">{i + 1}.</span>
                          <Input value={s.title} onChange={(e) => setProduksiSectionsForm((arr) => arr.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                            placeholder="Judul pasal" className="bg-[#0f0f12] border-white/10 h-9 flex-1" />
                          {PRODUKSI_AUTO_KINDS.includes(s.kind) && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30 shrink-0 whitespace-nowrap">Sebagian Otomatis</span>}
                          <button onClick={() => moveProduksiSection(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="rotate-90" /></button>
                          <button onClick={() => moveProduksiSection(i, 1)} disabled={i === produksiSectionsForm.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="-rotate-90" /></button>
                          <button onClick={() => removeProduksiSection(i)} className="text-red-400 hover:bg-red-500/10 rounded-lg p-1.5"><Trash2 size={15} /></button>
                        </div>
                        {s.kind === "biaya_termin" ? (
                          <p className="text-xs text-zinc-600 italic px-1">Isinya otomatis dari Item, Termin, Rekening, dan Denda Keterlambatan Pembayaran di form dokumen.</p>
                        ) : (
                          <>
                            {s.kind === "nilai_ppn" && <p className="text-xs text-zinc-600 italic px-1 mb-1.5">Nilai kontrak &amp; klausul PPN otomatis dari total item dan toggle "Kena PPN" di form dokumen. Teks di bawah ini bagian tambahan saja.</p>}
                            {s.kind === "jangka_waktu" && <p className="text-xs text-zinc-600 italic px-1 mb-1.5">Kalimat durasi otomatis dari field Durasi/Mulai Tanggal di form dokumen. Teks di bawah ini bagian tambahan saja.</p>}
                            {s.kind === "garansi" && <p className="text-xs text-zinc-600 italic px-1 mb-1.5">Klausul masa garansi otomatis dari field Masa Garansi di form dokumen. Teks di bawah ini bagian tambahan saja.</p>}
                            {s.kind === "lingkup_produksi" && <p className="text-xs text-zinc-600 italic px-1 mb-1.5">Tanggal pelaksanaan/pembongkaran event otomatis muncul di sini kalau Kategori Proyek = Event/Pameran. Teks di bawah ini bagian tambahan saja.</p>}
                            <textarea value={s.body} onChange={(e) => setProduksiSectionsForm((arr) => arr.map((x, j) => j === i ? { ...x, body: e.target.value } : x))}
                              rows={6} placeholder="Isi paragraf…" className="w-full bg-[#0f0f12] border border-white/10 rounded-lg p-2.5 text-sm" />
                          </>
                        )}
                      </div>
                    ))}
                    {produksiSectionsForm.length === 0 && <p className="text-xs text-zinc-600 italic text-center py-4">Belum ada pasal.</p>}
                    <Button onClick={addProduksiSection} variant="outline" className="w-full bg-[#18181B] border-white/10 h-9 text-sm"><Plus size={14} className="mr-1.5" />Tambah Pasal</Button>
                    <div className="flex gap-2 pt-1">
                      <Button onClick={saveProduksiSections} disabled={savingProduksiSections} className="bg-blue-600 h-10">{savingProduksiSections ? <Loader2 className="animate-spin" size={16} /> : "Simpan Pasal"}</Button>
                      <Button onClick={() => setProduksiSectionsForm(defaultProduksiSections)} variant="outline" className="bg-[#18181B] border-white/10 h-10">Batal</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {templateView === "custom-new" && (
            <div>
              <button onClick={() => setTemplateView("cards")} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 mb-3"><ChevronLeft size={14} /> Kembali ke Folder Dokumen</button>
              <div className="card-surface rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-1">Buat Kategori Dokumen Baru</h3>
                <p className="text-sm text-zinc-500 mb-4">Beri nama kategorinya (mis. "Kontrak C", "Berita Acara Serah Terima"), susun isinya, lalu simpan — otomatis jadi folder tersendiri di sini dan langsung bisa dipilih saat membuat dokumen untuk client. Setelah disimpan, form ini kosong lagi supaya siap dipakai bikin kategori berikutnya.</p>
                <div className="mb-4">
                  <Label className="text-[11px] text-zinc-400 uppercase">Nama Kategori Dokumen</Label>
                  <Input value={customBuilderName} onChange={(e) => setCustomBuilderName(e.target.value)} placeholder="mis. Kontrak C" className="bg-[#18181B] border-white/10 h-10 mt-1" />
                </div>
                <div className="space-y-3">
                  {customBuilderSections.map((s, i) => (
                    <div key={s.id || i} className="rounded-xl bg-[#18181B] border border-white/5 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-zinc-500 w-5 text-center shrink-0">{i + 1}.</span>
                        <Input value={s.title} onChange={(e) => setCustomBuilderSections((arr) => arr.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                          placeholder="Judul bagian" className="bg-[#0f0f12] border-white/10 h-9 flex-1" />
                        <button onClick={() => moveCustomBuilderSection(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="rotate-90" /></button>
                        <button onClick={() => moveCustomBuilderSection(i, 1)} disabled={i === customBuilderSections.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="-rotate-90" /></button>
                        <button onClick={() => removeCustomBuilderSection(i)} className="text-red-400 hover:bg-red-500/10 rounded-lg p-1.5"><Trash2 size={15} /></button>
                      </div>
                      <textarea value={s.body} onChange={(e) => setCustomBuilderSections((arr) => arr.map((x, j) => j === i ? { ...x, body: e.target.value } : x))}
                        rows={5} placeholder="Isi paragraf…" className="w-full bg-[#0f0f12] border border-white/10 rounded-lg p-2.5 text-sm" />
                    </div>
                  ))}
                  <Button onClick={addCustomBuilderSection} variant="outline" className="w-full bg-[#18181B] border-white/10 h-9 text-sm"><Plus size={14} className="mr-1.5" />Tambah Bagian</Button>
                  <Button onClick={saveNewCustomType} disabled={savingCustomType} className="bg-blue-600 h-10">{savingCustomType ? <Loader2 className="animate-spin" size={16} /> : "Simpan sebagai Folder Baru"}</Button>
                </div>
              </div>
            </div>
          )}

          {templateView.startsWith("custom:") && (() => {
            const typeId = templateView.slice(7);
            const current = customDocTypes.find((t) => t.type_id === typeId);
            return (
              <div>
                <button onClick={() => { setTemplateView("cards"); setEditCustomForm(null); }} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 mb-3"><ChevronLeft size={14} /> Kembali ke Folder Dokumen</button>
                <div className="card-surface rounded-2xl p-5">
                  <h3 className="font-heading font-semibold mb-1">{current?.name || "Kategori Dokumen"}</h3>
                  <p className="text-sm text-zinc-500 mb-4">Kategori dokumen kustom — ini cuma jadi <b>default awal</b> untuk dokumen baru dari kategori ini. Dokumen yang sudah pernah dibuat tidak pernah berubah walau default ini diedit lagi nanti.</p>
                  {!current || !editCustomForm ? <Loader2 className="animate-spin text-zinc-500" size={18} /> : (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-[11px] text-zinc-400 uppercase">Nama Kategori Dokumen</Label>
                        <Input value={editCustomForm.name} onChange={(e) => setEditCustomForm({ ...editCustomForm, name: e.target.value })} className="bg-[#18181B] border-white/10 h-10 mt-1" />
                      </div>
                      <div className="space-y-3">
                        {editCustomForm.sections.map((s, i) => (
                          <div key={s.id || i} className="rounded-xl bg-[#18181B] border border-white/5 p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs text-zinc-500 w-5 text-center shrink-0">{i + 1}.</span>
                              <Input value={s.title} onChange={(e) => setEditCustomForm((f) => ({ ...f, sections: f.sections.map((x, j) => j === i ? { ...x, title: e.target.value } : x) }))}
                                placeholder="Judul bagian" className="bg-[#0f0f12] border-white/10 h-9 flex-1" />
                              <button onClick={() => setEditCustomForm((f) => { const next = [...f.sections]; if (i === 0) return f; [next[i], next[i - 1]] = [next[i - 1], next[i]]; return { ...f, sections: next }; })} disabled={i === 0} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="rotate-90" /></button>
                              <button onClick={() => setEditCustomForm((f) => { const next = [...f.sections]; if (i === next.length - 1) return f; [next[i], next[i + 1]] = [next[i + 1], next[i]]; return { ...f, sections: next }; })} disabled={i === editCustomForm.sections.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30 p-1.5"><ChevronLeft size={15} className="-rotate-90" /></button>
                              <button onClick={() => setEditCustomForm((f) => ({ ...f, sections: f.sections.filter((_, j) => j !== i) }))} className="text-red-400 hover:bg-red-500/10 rounded-lg p-1.5"><Trash2 size={15} /></button>
                            </div>
                            <textarea value={s.body} onChange={(e) => setEditCustomForm((f) => ({ ...f, sections: f.sections.map((x, j) => j === i ? { ...x, body: e.target.value } : x) }))}
                              rows={5} placeholder="Isi paragraf…" className="w-full bg-[#0f0f12] border border-white/10 rounded-lg p-2.5 text-sm" />
                          </div>
                        ))}
                        <Button onClick={() => setEditCustomForm((f) => ({ ...f, sections: [...f.sections, { id: newSectionId(), title: "Bagian Baru", body: "", kind: "text" }] }))} variant="outline" className="w-full bg-[#18181B] border-white/10 h-9 text-sm"><Plus size={14} className="mr-1.5" />Tambah Bagian</Button>
                        <div className="flex items-center gap-2">
                          <Button onClick={() => saveEditCustomType(typeId)} disabled={savingEditCustomType} className="bg-blue-600 h-10">{savingEditCustomType ? <Loader2 className="animate-spin" size={16} /> : "Simpan Perubahan"}</Button>
                          <Button onClick={() => deleteCustomType(typeId, current.name)} disabled={deletingCustomType} variant="outline" className="h-10 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                            {deletingCustomType ? <Loader2 className="animate-spin" size={16} /> : <><Trash2 size={15} className="mr-1.5" />Hapus Kategori</>}
                          </Button>
                        </div>
                        {isAdmin && (
                          <p className="text-[10px] text-zinc-600">Sebagai Admin, penghapusan ini perlu disetujui Owner/Chief dulu sebelum benar-benar hilang.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </TabsContent>}

        {isAdminOrOwner && <TabsContent value="laporan" className="mt-4">
          <div className="card-surface rounded-2xl p-5 mb-3">
            <h3 className="font-heading font-semibold mb-1">Laporan Rekap Omset Otomatis</h3>
            <p className="text-sm text-zinc-500 mb-4">Laporan PDF &amp; Excel dikirim otomatis lewat email ke Owner &amp; Chief sesuai jadwal ini. Standar: setiap 7 hari, hari Sabtu jam 08:00.</p>
            <div className="grid gap-3">
              <div className="flex items-center justify-between card-surface rounded-xl p-3.5">
                <div>
                  <div className="text-sm font-medium">Aktifkan Laporan Terjadwal</div>
                  <p className="text-xs text-zinc-500 mt-0.5">Matikan untuk menjeda pengiriman otomatis tanpa menghapus pengaturan.</p>
                </div>
                <Switch checked={!!sf.enabled} disabled={togglingSchedule} onCheckedChange={toggleScheduleEnabled} className="shrink-0" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-zinc-500 mb-1 block">Interval (hari)</Label>
                  <Input type="number" min={1} max={90} value={sf.interval_days}
                    onChange={(e) => setScheduleForm({ ...sf, interval_days: e.target.value })}
                    className="bg-[#18181B] border-white/10 h-10" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500 mb-1 block">Jam Kirim</Label>
                  <div className="flex gap-1.5">
                    <Input type="number" min={0} max={23} value={sf.hour}
                      onChange={(e) => setScheduleForm({ ...sf, hour: e.target.value })}
                      className="bg-[#18181B] border-white/10 h-10 w-1/2" placeholder="Jam" />
                    <Input type="number" min={0} max={59} value={sf.minute}
                      onChange={(e) => setScheduleForm({ ...sf, minute: e.target.value })}
                      className="bg-[#18181B] border-white/10 h-10 w-1/2" placeholder="Menit" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-500 mb-1 block">Hari Pengiriman Pertama</Label>
                <Select value={String(sf.day_of_week)} onValueChange={(v) => setScheduleForm({ ...sf, day_of_week: Number(v) })}>
                  <SelectTrigger className="bg-[#18181B] border-white/10 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOW_OPTIONS.map((d) => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-zinc-600 mt-1">Setelah pengiriman pertama, jadwal berikutnya otomatis mengikuti interval hari di atas dari pengiriman terakhir.</p>
              </div>
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Laporan yang Disertakan</Label>
                <div className="space-y-1.5">
                  {REPORT_MODULE_OPTIONS.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                      <input type="checkbox" checked={sf.reports?.[m.key] ?? true}
                        onChange={(e) => setScheduleForm({ ...sf, reports: { ...(sf.reports || { omset: true, rekap_client: true }), [m.key]: e.target.checked } })}
                        className="accent-blue-600 w-4 h-4" />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
              {isAdmin && <p className="text-xs text-amber-400/80">Perubahan jadwal oleh Admin perlu persetujuan Owner/Chief dulu sebelum aktif.</p>}
              <div className="flex gap-2">
                <Button onClick={saveSchedule} disabled={savingSchedule} className="bg-blue-600 h-10">
                  {savingSchedule ? <Loader2 size={16} className="mr-1 animate-spin" /> : null}Simpan Jadwal
                </Button>
                {scheduleForm && <Button onClick={() => setScheduleForm(null)} variant="outline" className="bg-[#18181B] border-white/10 h-10">Batal</Button>}
              </div>
              <div className="border-t border-white/5 pt-3 flex items-center justify-between gap-3">
                <p className="text-xs text-zinc-500">
                  {reportSchedule?.last_sent_at
                    ? `Terakhir terkirim: ${new Date(reportSchedule.last_sent_at).toLocaleString("id-ID")}`
                    : "Belum pernah terkirim."}
                </p>
                {isOwnerOrChief && (
                  <Button onClick={sendReportNow} disabled={sendingNow} variant="outline" className="bg-[#18181B] border-white/10 h-9 shrink-0">
                    {sendingNow ? <Loader2 size={15} className="mr-1 animate-spin" /> : null}Kirim Sekarang
                  </Button>
                )}
              </div>
            </div>
          </div>
        </TabsContent>}

        {isOwnerOrChief && <TabsContent value="rab-kalkulator" className="mt-4">
          <KalkulatorMasterData />
        </TabsContent>}

        {isAdminOrOwner && <TabsContent value="otomasi-pipeline" className="mt-4">
          <PipelineAutomationSettings />
        </TabsContent>}

        {isOwnerOrChief && <TabsContent value="backup" className="mt-4">
          <BackupPanel />
        </TabsContent>}

        {canManageUsers && <TabsContent value="users" className="mt-4">
          <Button data-testid="add-user-btn" onClick={() => setUserOpen(true)} className="mb-3 bg-gradient-to-r from-blue-700 to-blue-900 h-9"><Plus size={16} className="mr-1" />Tambah User</Button>
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.user_id} className="card-surface rounded-xl p-3.5 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{u.name} <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 ml-1 capitalize">{u.role}</span></div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </div>
                <button data-testid={`del-user-${u.user_id}`} onClick={() => delUser(u.user_id)} className="text-red-400 p-2 hover:bg-red-500/10 rounded-lg"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </TabsContent>}

        {!isManager && <TabsContent value="branches" className="mt-4">
          <div className="flex gap-2 mb-3">
            <Input data-testid="branch-input" value={branchName} onChange={(e) => setBranchName(e.target.value)} placeholder="Nama cabang" className="bg-[#18181B] border-white/10 h-10" />
            <Button data-testid="add-branch-btn" onClick={addBranch} className="bg-blue-600 h-10"><Plus size={16} /></Button>
          </div>
          <div className="space-y-2">
            {branches.map((b) => (
              <div key={b.branch_id} className="card-surface rounded-xl p-3.5 flex items-center justify-between">
                <span className="text-sm font-medium">{b.name}</span>
                <button onClick={() => delBranch(b.branch_id)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </TabsContent>}

        {!isManager && <TabsContent value="master" className="mt-4">
          <div className="flex gap-2 mb-4">
            <Select value={masterType} onValueChange={setMasterType}>
              <SelectTrigger className="w-44 bg-[#18181B] border-white/10 h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181B] border-white/10">{MASTER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="master-input" value={masterVal} onChange={(e) => setMasterVal(e.target.value)} placeholder="Nilai baru" className="bg-[#18181B] border-white/10 h-10" />
            <Button data-testid="add-master-btn" onClick={addMaster} className="bg-blue-600 h-10"><Plus size={16} /></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(master[masterType] || []).map((m) => (
              <span key={m.master_id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#18181B] border border-white/10 text-sm">
                {m.value}
                <button onClick={() => delMaster(m.master_id)} className="text-zinc-500 hover:text-red-400"><X size={13} /></button>
              </span>
            ))}
          </div>
        </TabsContent>}

        {!isManager && <TabsContent value="excel" className="mt-4 space-y-3">
          <div className="card-surface rounded-2xl p-5">
            <h3 className="font-heading font-semibold mb-1">Template Import</h3>
            <p className="text-sm text-zinc-500 mb-3">Download template Excel-nya dulu, isi/copy-paste data leads dari file lama kamu ke format ini (judul kolom jangan diubah), baru upload di bawah. Ada sheet "Petunjuk" di dalamnya.</p>
            <Button onClick={downloadTemplate} variant="outline" className="bg-[#18181B] border-white/10 h-10"><Download size={16} className="mr-1.5" />Download Template Excel</Button>
          </div>
          <div className="card-surface rounded-2xl p-5">
            <h3 className="font-heading font-semibold mb-1">Import Leads</h3>
            <p className="text-sm text-zinc-500 mb-3">Upload file Excel (.xlsx) yang sudah diisi sesuai template. Duplikat berdasarkan nama/no HP otomatis di-skip.</p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={doImport} className="hidden" data-testid="import-file" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-blue-600 h-10">
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <><Upload size={16} className="mr-1.5" />Pilih File Excel</>}
            </Button>
            {importResult && (
              <div className="mt-3 text-sm">
                <p className="text-zinc-300">{importResult.imported} baris berhasil masuk · {importResult.skipped} dilewati (duplikat)</p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto hide-scrollbar rounded-lg bg-[#18181B] border border-white/10 p-2 space-y-1">
                    {importResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-amber-400">Baris {e.row}: {e.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="card-surface rounded-2xl p-5">
            <h3 className="font-heading font-semibold mb-1">Export Leads</h3>
            <p className="text-sm text-zinc-500 mb-3">Download semua leads ke file Excel.</p>
            <Button data-testid="export-btn" onClick={doExport} variant="outline" className="bg-[#18181B] border-white/10 h-10"><Download size={16} className="mr-1.5" />Export ke Excel</Button>
          </div>
        </TabsContent>}

        {!isManager && <TabsContent value="wa" className="mt-4 space-y-3">
          <div className="card-surface rounded-2xl p-5">
            <h3 className="font-heading font-semibold mb-1">Template WhatsApp Siap Pakai</h3>
            <p className="text-sm text-zinc-500 mb-3">Buat pesan follow-up siap pakai. Gunakan <span className="text-blue-300">{"{nama}"}</span> untuk nama customer. Kode (mis. <span className="text-blue-300">/pesanfollowup</span>) dipakai sebagai pintasan saat chat.</p>
            <div className="grid gap-2 mb-2">
              <Input data-testid="wa-code" value={waForm.code} onChange={(e) => setWaForm({ ...waForm, code: e.target.value })} placeholder="/kodepesan (mis. /pesanpenagihan)" className="bg-[#18181B] border-white/10 h-10" />
              <Input data-testid="wa-title" value={waForm.title} onChange={(e) => setWaForm({ ...waForm, title: e.target.value })} placeholder="Judul singkat" className="bg-[#18181B] border-white/10 h-10" />
              <textarea data-testid="wa-message" value={waForm.message} onChange={(e) => setWaForm({ ...waForm, message: e.target.value })} placeholder="Isi pesan… Halo {nama}, ..." rows={3} className="bg-[#18181B] border border-white/10 rounded-lg p-2.5 text-sm" />
              <div className="flex gap-2">
                <Button data-testid="add-wa-btn" onClick={addWa} className="bg-blue-600 h-10 w-fit"><Plus size={16} className="mr-1" />{waEditId ? "Update Template" : "Simpan Template"}</Button>
                {waEditId && <Button data-testid="cancel-wa-btn" onClick={cancelWa} variant="outline" className="bg-[#18181B] border-white/10 h-10 w-fit">Batal</Button>}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {waTemplates.map((t) => (
              <div key={t.tpl_id} className="card-surface rounded-xl p-3.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">{t.code}</span><span className="text-sm font-medium">{t.title}</span></div>
                  <p className="text-xs text-zinc-500 mt-1">{t.message}</p>
                </div>
                <button data-testid={`edit-wa-${t.tpl_id}`} onClick={() => editWa(t)} className="text-blue-300 p-1.5 hover:bg-white/5 rounded-lg shrink-0"><Pencil size={14} /></button>
                <button data-testid={`del-wa-${t.tpl_id}`} onClick={() => delWa(t.tpl_id)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg shrink-0"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </TabsContent>}
        </div>
      </Tabs>

      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent className="bg-[#0f0f12] border-white/10">
          <DialogHeader><DialogTitle className="font-heading">Tambah User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-zinc-400 uppercase">Nama</Label><Input data-testid="user-name" value={uform.name} onChange={(e) => setUform({ ...uform, name: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" /></div>
            <div><Label className="text-xs text-zinc-400 uppercase">Email</Label><Input data-testid="user-email" type="email" value={uform.email} onChange={(e) => setUform({ ...uform, email: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" /></div>
            <div><Label className="text-xs text-zinc-400 uppercase">Password</Label><Input data-testid="user-password" type="text" value={uform.password} onChange={(e) => setUform({ ...uform, password: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-zinc-400 uppercase">Role</Label>
                <Select value={uform.role} onValueChange={(v) => setUform({ ...uform, role: v })}>
                  <SelectTrigger data-testid="user-role" className="bg-[#18181B] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10">{roleOptions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {uform.role === "manager" ? (
                <div><Label className="text-xs text-zinc-400 uppercase">Cabang Dikelola</Label>
                  <Select value={uform.managed_branch_id || undefined} onValueChange={(v) => setUform({ ...uform, managed_branch_id: v })}>
                    <SelectTrigger data-testid="user-managed-branch" className="bg-[#18181B] border-white/10 mt-1"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">{branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : uform.role === "sales" ? (
                <div><Label className="text-xs text-zinc-400 uppercase">Cabang</Label>
                  <Select value={uform.branch_id || undefined} onValueChange={(v) => setUform({ ...uform, branch_id: v })}>
                    <SelectTrigger className="bg-[#18181B] border-white/10 mt-1"><SelectValue placeholder="-" /></SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">{branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
            <Button data-testid="save-user" onClick={addUser} className="w-full bg-gradient-to-r from-blue-700 to-blue-900">Buat User</Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
